import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import { motion } from 'framer-motion';
import apiManager from '../../../../lib/api';
import { AnalyticsSummary } from '../../../../lib/api/services/analytics';

const AnalyticsPage = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  useEffect(() => {
    const fetchSummary = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Obtener datos reales del backend
        const response = await apiManager.analytics.getAnalyticsSummary({
          period: dateRange
        });
        
        if (response.success && response.data) {
          setSummary(response.data);
        } else {
          setError(response.error?.message || 'Error al cargar los datos analíticos');
        }
      } catch (err) {
        console.error('Error al cargar el resumen analítico:', err);
        setError('Error al cargar los datos. Inténtalo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSummary();
  }, [dateRange]);
  
  // Variantes para animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  
  return (
    <AdminLayout title="Analíticas">
      {/* Selector de rango de fechas */}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Resumen de Rendimiento</h2>
        <div className="flex bg-white rounded-md shadow-sm">
          <button
            onClick={() => setDateRange('week')}
            className={`px-4 py-2 text-sm rounded-l-md ${
              dateRange === 'week' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => setDateRange('month')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'month' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => setDateRange('quarter')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'quarter' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Trimestre
          </button>
          <button
            onClick={() => setDateRange('year')}
            className={`px-4 py-2 text-sm rounded-r-md ${
              dateRange === 'year' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Año
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          <p>{error}</p>
        </div>
      ) : summary ? (
        <>
          {/* Métricas principales */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Ingresos Totales</span>
                <span className="text-3xl font-bold text-gray-900">
                  {summary.totalRevenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
                <div className={`flex items-center mt-2 ${summary.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="text-xs font-medium">
                    {summary.revenueGrowth >= 0 ? '+' : ''}{summary.revenueGrowth.toFixed(1)}%
                  </span>
                  <svg 
                    className="h-4 w-4 ml-1" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    {summary.revenueGrowth >= 0 ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    )}
                  </svg>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Pedidos</span>
                <span className="text-3xl font-bold text-gray-900">
                  {summary.totalOrders}
                </span>
                <div className={`flex items-center mt-2 ${summary.orderGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="text-xs font-medium">
                    {summary.orderGrowth >= 0 ? '+' : ''}{summary.orderGrowth.toFixed(1)}%
                  </span>
                  <svg 
                    className="h-4 w-4 ml-1" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    {summary.orderGrowth >= 0 ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    )}
                  </svg>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Usuarios</span>
                <span className="text-3xl font-bold text-gray-900">
                  {summary.totalUsers}
                </span>
                <span className="text-xs text-gray-500 mt-2">
                  {summary.activeUsers} activos
                </span>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              variants={itemVariants}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Tasa de Conversión</span>
                <span className="text-3xl font-bold text-gray-900">
                  {summary.conversionRate.toFixed(2)}%
                </span>
                <span className="text-xs text-gray-500 mt-2">
                  Visitas a compras
                </span>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Tarjetas para navegar a las diferentes secciones */}
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Informes Detallados</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/admin/analytics/sales" className="block group">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 group-hover:shadow-md group-hover:border-indigo-300">
                <div className="mb-4 w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">Ventas</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Análisis de ventas, tendencias y patrones temporales
                </p>
              </div>
            </Link>
            
            <Link to="/admin/analytics/products" className="block group">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 group-hover:shadow-md group-hover:border-indigo-300">
                <div className="mb-4 w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">Productos</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Rendimiento de productos, análisis de categorías y stock
                </p>
              </div>
            </Link>
            
            <Link to="/admin/analytics/customers" className="block group">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 group-hover:shadow-md group-hover:border-indigo-300">
                <div className="mb-4 w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">Clientes</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Comportamiento de usuarios, segmentación y lealtad
                </p>
              </div>
            </Link>
            
            <Link to="/admin/analytics/funnel" className="block group">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 transition-all duration-200 group-hover:shadow-md group-hover:border-indigo-300">
                <div className="mb-4 w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M3 8h18M3 12h18M3 16h18M3 20h18" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">Embudo de Conversión</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Análisis del embudo de ventas y tasas de conversión
                </p>
              </div>
            </Link>
          </div>
        </>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No hay datos disponibles</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default AnalyticsPage; 